'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useModalA11y } from '@/hooks/use-modal-a11y';
import { IconButton } from './button';

type SheetSide = 'left' | 'right' | 'bottom';

const SIDE: Record<SheetSide, string> = {
  left: 'animate-slide-in-left inset-y-0 left-0 w-[min(20rem,88vw)] border-r',
  right: 'animate-slide-in-right inset-y-0 right-0 w-[min(20rem,88vw)] border-l',
  bottom: 'animate-slide-in-up inset-x-0 bottom-0 max-h-[85dvh] border-t',
};

interface SheetProps {
  /** DOM id of the dialog, for a trigger's `aria-controls`. */
  id?: string;
  open: boolean;
  onClose: () => void;
  /** Accessible name, shown in the header unless `hideTitle`. */
  title: string;
  hideTitle?: boolean;
  /** Edge it slides in from. `bottom` for filters/detail on phones, `left` for navigation. */
  side?: SheetSide;
  /** Right-aligned action row pinned under the scrollable body. */
  footer?: ReactNode;
  /** Body padding; off for content that brings its own (the nav drawer). */
  padded?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Edge-anchored panel over a backdrop (focus trap, Escape, scroll lock via
 * useModalA11y). Renders nothing while closed. z-50, the same slot as Modal.
 * The body is a flex column, so a child with `flex-1 overflow-y-auto` scrolls
 * on its own while its siblings stay put.
 */
export function Sheet({
  id,
  open,
  onClose,
  title,
  hideTitle = false,
  side = 'bottom',
  footer,
  padded = true,
  children,
  className,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useModalA11y(open, panelRef, onClose);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      data-modal-overlay="true"
      className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={hideTitle ? title : undefined}
        aria-labelledby={hideTitle ? undefined : titleId}
        className={cn(
          'safe-top safe-bottom fixed flex flex-col border-zinc-700 bg-zinc-900 shadow-2xl shadow-black/50',
          SIDE[side],
          className
        )}
      >
        <header className={cn('flex shrink-0 items-center gap-3 px-4 pt-3 pb-2', hideTitle && 'justify-end')}>
          {hideTitle ? null : (
            <h2 id={titleId} className="min-w-0 flex-1 truncate text-base font-semibold text-zinc-50">
              {title}
            </h2>
          )}
          <IconButton icon={X} label="Close" size="sm" onClick={onClose} />
        </header>
        <div className={cn('thin-scroll flex min-h-0 flex-1 flex-col overflow-y-auto', padded && 'px-4 pb-4')}>
          {children}
        </div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-zinc-800 px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
